import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { TabletButton } from '@campfire/tablet-button';
import { Box, Switch, TextField, Typography } from '@material-ui/core';
import { Message } from '@material-ui/icons';
import { convertFromRaw, convertToRaw, EditorState } from 'draft-js';
import React, { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { GET_PRE_APPLICATION_CONTENT } from './admin-console-applicant-screening.gql';

interface PreApplicationContentData {
  vm: {
    preApplicationContent: {
      preApplicationContentId: string;
      content: string | null;
      videoUrl: string | null;
      isEnabled: boolean;
    } | null;
  };
}

export const PreApplicationEditForm = () => {
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const { run } = useCampfireFetch({ defer: true });

  const { data, loading } = useCampfireQuery<PreApplicationContentData, undefined>(GET_PRE_APPLICATION_CONTENT);

  const [isEnabled, setIsEnabled] = useState(false);
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [videoUrl, setVideoUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const existingContent = useMemo(() => data?.vm.preApplicationContent, [data]);

  useEffect(() => {
    if (!existingContent) return;
    setIsEnabled(existingContent.isEnabled);
    setVideoUrl(existingContent.videoUrl ?? '');
    if (existingContent.content) {
      try {
        setEditorState(EditorState.createWithContent(convertFromRaw(JSON.parse(existingContent.content))));
      } catch {
        setEditorState(EditorState.createEmpty());
      }
    }
  }, [existingContent]);

  const handleSave = () => {
    const contentRaw = editorState.getCurrentContent().hasText()
      ? JSON.stringify(convertToRaw(editorState.getCurrentContent()))
      : null;

    setIsSaving(true);
    run({
      url: `${apiUrl}/vm/application/pre-application-content/save`,
      method: 'post',
      data: {
        content: contentRaw,
        videoUrl: videoUrl.trim() || null,
        isEnabled,
      },
    })
      .then((res) => {
        setSnackbar({
          open: true,
          message: res.ok ? 'Pre-application content saved' : 'Failed to save',
          variant: res.ok ? 'success' : 'error',
        });
      })
      .catch(() => {
        setSnackbar({ open: true, message: 'Network error', variant: 'error' });
      })
      .finally(() => setIsSaving(false));
  };

  if (loading) return null;

  return (
    <Box>
      <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
        <Message style={{ fontSize: '14px', paddingRight: '5px' }} /> Pre-Application Content
      </Typography>
      <Typography variant='body2' style={{ fontSize: '14px', color: '#808080' }}>
        Show a content block and/or video to new volunteers before they start their profile
      </Typography>

      <Box display='flex' flex='1 1 auto' marginTop='1.5rem'>
        <Box style={{ paddingTop: 4, paddingRight: 8 }}>
          <Switch color='primary' checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
        </Box>
        <Box>
          <Box ml={1}>
            <Typography style={{ fontWeight: 500, fontSize: '14px', color: '#333' }}>
              {`Enable Pre-Application Content (${isEnabled ? 'On' : 'Off'})`}
            </Typography>
            <Typography style={{ fontSize: '12px', color: '#808080' }}>
              {isEnabled
                ? 'Volunteers will see this content before filling in their profile.'
                : 'Volunteers will go straight to the profile form.'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {isEnabled && (
        <Box marginTop='1.5rem'>
          <Box marginBottom='1.5rem'>
            <Typography style={{ fontWeight: 500, fontSize: '14px', color: '#333', marginBottom: 8 }}>
              Content
            </Typography>
            <Box border='1px solid #ddd' borderRadius={4} padding='8px 12px' minHeight={120}>
              <CampfireRichEditor
                placeholder='Enter content to display to volunteers...'
                editorState={editorState}
                setEditorState={setEditorState}
              />
            </Box>
          </Box>

          <Box marginBottom='1.5rem'>
            <Typography style={{ fontWeight: 500, fontSize: '14px', color: '#333', marginBottom: 8 }}>
              Video URL
            </Typography>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              placeholder='https://www.youtube.com/watch?v=... or https://vimeo.com/...'
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
            />
            <Typography style={{ fontSize: '12px', color: '#808080', marginTop: 4 }}>
              Supports YouTube and Vimeo links. Leave blank to show content only.
            </Typography>
          </Box>
        </Box>
      )}

      <Box marginTop='1rem' display='flex' justifyContent='flex-end'>
        <TabletButton variant='contained' color='primary' onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </TabletButton>
      </Box>
    </Box>
  );
};
